import { useState } from "react";
import { useCreateTenantUser } from "@/api/tenant-users";
import { Button } from "@/components/ui/button";
import { Dialog, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";

export function AddUserDialog({
  open,
  onClose,
  tenantId,
  defaultRole = "client_viewer",
}: {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  defaultRole?: string;
}) {
  const createUser = useCreateTenantUser(tenantId);
  const [form, setForm] = useState({
    email: "",
    first_name: "",
    last_name: "",
    role: defaultRole,
    password: "",
  });

  const isTenantMember = form.role === "tenant_member";

  const fieldErrors = createUser.isError
    ? (createUser.error as { response?: { data?: { error?: { details?: Record<string, string[]> } } } })
        ?.response?.data?.error?.details ?? {}
    : {};
  const generalError = createUser.isError && Object.keys(fieldErrors).length === 0
    ? (createUser.error as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message || "Something went wrong. Please try again."
    : null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createUser.reset();
    const payload = isTenantMember
      ? { email: form.email, first_name: form.first_name, last_name: form.last_name, role: form.role }
      : form;
    createUser.mutate(payload, {
      onSuccess: () => {
        onClose();
        setForm({ email: "", first_name: "", last_name: "", role: defaultRole, password: "" });
      },
    });
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Create User</DialogTitle>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-text-muted">Email</label>
          <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required autoFocus />
          {fieldErrors.email && <p className="mt-1 text-xs text-danger">{fieldErrors.email[0]}</p>}
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-text-muted">First Name</label>
            <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
            {fieldErrors.first_name && <p className="mt-1 text-xs text-danger">{fieldErrors.first_name[0]}</p>}
          </div>
          <div>
            <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-text-muted">Last Name</label>
            <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
            {fieldErrors.last_name && <p className="mt-1 text-xs text-danger">{fieldErrors.last_name[0]}</p>}
          </div>
        </div>
        <div>
          <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-text-muted">Role</label>
          <Select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
            <option value="client_viewer">Client Viewer</option>
            <option value="client_admin">Client Admin</option>
            <option value="tenant_member">Tenant Member</option>
          </Select>
          {fieldErrors.role && <p className="mt-1 text-xs text-danger">{fieldErrors.role[0]}</p>}
          {isTenantMember && (
            <p className="mt-1.5 text-xs text-text-muted">
              Tenant members have no login access. They are placeholders for users synced from integrations.
            </p>
          )}
        </div>
        {!isTenantMember && (
          <div>
            <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-text-muted">Password</label>
            <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={8} />
            {fieldErrors.password && <p className="mt-1 text-xs text-danger">{fieldErrors.password[0]}</p>}
          </div>
        )}
        {generalError && <p className="text-sm text-danger">{generalError}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="outline" type="button" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={createUser.isPending}>{createUser.isPending ? "Creating..." : "Create"}</Button>
        </div>
      </form>
    </Dialog>
  );
}
